<template>
  <div class="generic-fragment">
    <div class="header">
      <div class="text">
        <h1>{{ title }}</h1>
        <p class="description">{{ description }}</p>
        <p class="import-path">
          Imports:
          <code>http://localhost:8080/js/{{ scriptName }}.js</code>
        </p>
      </div>

      <div class="status-wrapper">
        <div class="status-label">Module Script Status</div>
        <div
          class="status"
          :class="statusClass"
          @click="scriptStatus === 'Failed' && openErrorModal()"
          :title="scriptStatus === 'Failed' ? 'Click to see error details' : ''"
        >
          {{ scriptStatus.toUpperCase() }}
          <span v-if="loadDuration !== null" class="duration">
            · {{ loadDuration }}ms
          </span>
        </div>
      </div>
    </div>

    <div class="preview-wrapper">
      <div class="preview-box" :style="{ width: previewWidth + 'px' }">
        <!-- Overlay -->
        <div class="slot-overlay">
          <slot />
        </div>

        <!-- Background -->
        <div class="feegow-background" v-if="props.useBackground">
          <img
            src="@/assets/feegow-background.jpg"
            alt="Simulated Background"
          />
        </div>

        <!-- Resizer Handle -->
        <div class="resizer" @mousedown="initResize"></div>
      </div>
    </div>

    <!-- Error Modal -->
    <div
      v-if="showErrorPopup"
      class="error-modal"
      @click.self="showErrorPopup = false"
    >
      <div class="error-content">
        <h2>Script Load Failed</h2>
        <p>We couldn't load one or more required scripts:</p>
        <ul>
          <li v-for="src in scriptsToLoad" :key="src">
            <code>http://localhost:8080/js/{{ src }}</code>
          </li>
        </ul>
        <p>Please check your dev server and try again.</p>

        <div class="modal-buttons">
          <button @click="retryLoad">Retry</button>
          <button @click="showErrorPopup = false">Close</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onMounted, ref, defineProps, computed } from "vue";

const props = defineProps<{
  title: string;
  description: string;
  scriptName: string;
  useBackground?: boolean;
}>();

const scriptStatus = ref("Loading");
const showErrorPopup = ref(false);
const loadStart = ref<number | null>(null);
const loadDuration = ref<number | null>(null);

const scriptsToLoad = [
  "chunk-vendors.js",
  "chunk-common.js",
  `${props.scriptName}.js`,
];

const statusClass = computed(() => {
  return {
    loading: scriptStatus.value === "Loading",
    loaded: scriptStatus.value === "Loaded",
    failed: scriptStatus.value === "Failed",
  };
});

function appendScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `http://localhost:8080/js/${src}`;
    script.async = true;
    script.defer = true;

    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load: ${src}`));

    document.body.appendChild(script);
  });
}

async function loadScripts() {
  scriptStatus.value = "Loading";
  loadStart.value = performance.now();
  try {
    for (const script of scriptsToLoad) {
      await appendScript(script);
    }
    scriptStatus.value = "Loaded";
  } catch (err) {
    scriptStatus.value = "Failed";
  } finally {
    loadDuration.value = Math.round(performance.now() - (loadStart.value || 0));
  }
}

function retryLoad() {
  showErrorPopup.value = false;
  loadScripts();
}

function openErrorModal() {
  showErrorPopup.value = true;
}

// Simulate user session
document.userProperties = {
  idInTable: 8,
  userId: 1,
  licencaID: 17973,
  tipoUsuario: "profissionais",
};

const previewWidth = ref(Math.floor(window.innerWidth * 0.98));

function initResize(e: MouseEvent) {
  const startX = e.clientX;
  const startWidth = previewWidth.value;

  function onMouseMove(e: MouseEvent) {
    const delta = e.clientX - startX;
    previewWidth.value = Math.max(320, startWidth + delta); // minimum width
  }

  function stopResize() {
    window.removeEventListener("mousemove", onMouseMove);
    window.removeEventListener("mouseup", stopResize);
  }

  window.addEventListener("mousemove", onMouseMove);
  window.addEventListener("mouseup", stopResize);
}

onMounted(() => {
  loadScripts();
});
</script>

<style scoped lang="scss">
.generic-fragment {
  padding: 40px;
  max-width: 99vw;
  margin: 0 auto;
  font-family: Avenir, Helvetica, Arial, sans-serif;
}

.header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;

  .text {
    max-width: 750px;

    h1 {
      font-size: 28px;
      margin-bottom: 8px;
    }

    .description {
      font-size: 16px;
      color: #444;
    }

    .import-path {
      font-size: 14px;
      margin-top: 8px;

      code {
        background-color: #f5f5f5;
        padding: 2px 6px;
        border-radius: 4px;
        font-family: monospace;
      }
    }
  }
}

.status-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;

  .status-label {
    font-size: 11px;
    color: #888;
    font-weight: 500;
  }

  .status {
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    text-transform: uppercase;
    white-space: nowrap;
    border: 1px solid transparent;
    transition: all 0.2s ease;
    cursor: default;

    &.loading {
      background-color: #fff3cd;
      color: #856404;
      border-color: #ffeeba;
    }

    &.loaded {
      background-color: #e6f4ea;
      color: #2a7a47;
      border-color: #b8e2ca;
    }

    &.failed {
      background-color: #fdecea;
      color: #b71c1c;
      border-color: #f5c6cb;
      cursor: pointer;
    }

    .duration {
      font-weight: normal;
      font-size: 12px;
      color: #777;
      margin-left: 6px;
    }
  }
}

.preview-wrapper {
  display: flex;
  justify-content: center;
  overflow-x: auto;
  padding-bottom: 24px;
}

.preview-box {
  position: relative;
  min-height: 600px;
  height: 80vh;
  min-width: 320px;
  width: 98vw;
  max-width: 2400px;
  overflow: auto;
  border: 1px solid #ddd;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
}

.resizer {
  position: absolute;
  top: 0;
  right: 0;
  width: 8px;
  height: 100%;
  cursor: ew-resize;
  background: rgba(0, 0, 0, 0);
  z-index: 10;
}

.resizer:hover {
  background: rgba(0, 0, 0, 0.05);
}

.preview-box::-webkit-scrollbar {
  height: 8px;
}
.preview-box::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 4px;
}

/* 👇 Content injected via <slot> floats over image */
.slot-overlay {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 2;
  width: 100%;
  padding: 16px;
  display: flex;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
  pointer-events: auto;
}

/* 👇 Background mockup stays below */
.feegow-background {
  position: relative;
  z-index: 1;
}

.feegow-background img {
  width: 100%;
  display: block;
}

/* Modal Styling */
.error-modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;

  .error-content {
    background: white;
    padding: 24px 32px;
    border-radius: 10px;
    max-width: 500px;
    width: 90%;
    text-align: center;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);

    h2 {
      margin-bottom: 16px;
      font-size: 20px;
    }

    ul {
      text-align: left;
      margin: 16px 0;
    }

    li {
      margin-bottom: 6px;
    }

    code {
      background-color: #f1f1f1;
      padding: 2px 6px;
      border-radius: 4px;
      font-size: 13px;
    }

    .modal-buttons {
      display: flex;
      justify-content: center;
      gap: 12px;
      margin-top: 20px;

      button {
        padding: 6px 16px;
        font-size: 14px;
        border: none;
        border-radius: 6px;
        cursor: pointer;
        transition: background-color 0.2s ease;
      }

      button:first-child {
        background-color: #3498db;
        color: white;
      }

      button:first-child:hover {
        background-color: #2980b9;
      }

      button:last-child {
        background-color: #e0e0e0;
        color: #333;
      }

      button:last-child:hover {
        background-color: #cacaca;
      }
    }
  }
}
</style>
